<template>
	<Application class="ContractTemplatesView" :loading="loading" v-model:error-title="errorTitle" :error-detail="errorDetail">
		<template #navbar>
			<v-text-field variant="outlined" density="compact" clearable hide-details
				class="search"
				:placeholder="$t('text.search')"
				v-model="searchString"
				@keyup.enter="search()"
				@click:clear="search()"
				data-cy="search"
			>
				<template #prepend-inner>
					<v-icon :size="20">mdi-magnify</v-icon>
				</template>
			</v-text-field>
			<v-menu>
				<template #activator="{ props }">
					<v-btn
						v-bind="props"
						data-cy="addTemplateButtons"
						size="small"
						class="blueButton"
						elevation="0"
					>
						<v-icon>mdi-plus</v-icon>
						<span class="d-none d-md-inline">{{ $t('text.addTemplate') }}</span>
					</v-btn>
				</template>
				<v-list>
					<v-list-item
						@click="$router.push({ name: 'contractTemplate', query: { type: ContractSequenceType.SERVICE_PROVIDER } })"
					>
						<v-list-item-title>{{ $t('text.serviceProviderContract') }}</v-list-item-title>
					</v-list-item>
					<IfFeatureFlag flag="FEATURE_TRADE_HUB">
						<v-list-item v-if="featureEnabled(Features.TRADE_HUB)"
							@click="$router.push({ name: 'contractTemplate', query: { type: ContractSequenceType.TOUR_OPERATOR } })"
						>
							<v-list-item-title>{{ $t('text.tourOperatorContract') }}</v-list-item-title>
						</v-list-item>
					</IfFeatureFlag>
				</v-list>
			</v-menu>
		</template>

		<ContractTemplatesList
			:items="displayedTemplates"
			:total="total"
			:limit="limit"
			:offset="offset"
			:sort-by="sortBy"
			@update:limit="updateLimit"
			@update:offset="updateOffset"
			@update:sortBy="updateSortBy"
			style="margin-top: 64px;"
		/>
	</Application>
</template>

<script lang="ts">
import Application from '../Application.vue'
import ContractTemplatesList from '../../../components/contract/ContractTemplatesList.vue'
import ContractExecutive from '../../../../../api2/src/modules/contract/ContractExecutive'
import IfFeatureFlag from '@/components/ifFeatureFlag/IfFeatureFlag.vue'
import { ContractSequenceType } from '../../../../../shared/types/account-types'
import type { ContractTemplateForList } from '../../../../../api2/src/modules/contract/ContractTechnical'
import Common from '../../../mixins/Common.vue'
import { Features } from '../../../../../shared/features'

export default {
	components: { ContractTemplatesList, Application, IfFeatureFlag },
	mixins: [ Common ],
	data: () => ({
		templates: [] as ContractTemplateForList[],
		displayedTemplates: [] as ContractTemplateForList[],
		loading: false,
		errorTitle: '',
		errorDetail: '',
		limit: 10,
		offset: 0,
		total: 0,
		searchString: '',
		sortBy: [],
		ContractSequenceType,
		Features,
	}),
	methods: {
		async getTemplates() {
			try {
				this.loading = true
				let contractExecutive = new ContractExecutive(this)
				this.templates = await contractExecutive.getContractTemplatesForList(this.$store.state.selectedClient.sys.id, this.$store.state.loggedInUser.fields.canSeeObjectsWithTags.de)
				this.total = this.templates.length
				this.applyFilters()
			}
			catch (error) {
				this.errorTitle = this.$t('text.ERROR')
				this.errorDetail = error.response ? error.response.error : error
			}
			finally {
				this.loading = false
			}
		},
		applyFilters() {
			let filteredTemplates = [...this.templates]

			if (this.searchString) {
				const searchTerm = this.searchString.toLowerCase()
				filteredTemplates = filteredTemplates.filter(template => {
					// Search through all locales of the name
					return Object.values(template.name).some(localeName => 
						localeName?.toLowerCase().includes(searchTerm)
					)
				})
			}

			if (this.sortBy && this.sortBy.length) {
				filteredTemplates.sort((a, b) => {
					for (const { key, order } of this.sortBy) {
						const valueA = a[key]
						const valueB = b[key]

						if (valueA == null && valueB == null) continue
						if (valueA == null) return order === "asc" ? 1 : -1
						if (valueB == null) return order === "asc" ? -1 : 1

						if (Array.isArray(valueA) && Array.isArray(valueB)) {
							const firstA = valueA[0] || ''
							const firstB = valueB[0] || ''
							if (firstA < firstB) return order === "asc" ? -1 : 1
							if (firstA > firstB) return order === "asc" ? 1 : -1
						} else {
							if (valueA < valueB) return order === "asc" ? -1 : 1
							if (valueA > valueB) return order === "asc" ? 1 : -1
						}
					}
					return 0
				})
			}

			const start = this.offset
			const end = this.offset + this.limit
			this.displayedTemplates = filteredTemplates.slice(start, end)
			this.total = filteredTemplates.length
		},
		updateLimit(limit) {
			this.limit = limit
			this.offset = 0
			this.applyFilters()
		},
		updateOffset(offset) {
			this.offset = offset
			this.applyFilters()
		},
		updateSortBy(sortBy) {
			this.sortBy = sortBy
			this.offset = 0
			this.applyFilters()
		},
		search() {
			this.offset = 0
			this.applyFilters()
		},
	},
	mounted() {
		this.getTemplates()
	},
}
</script>

<style scoped lang="scss">
.ContractTemplatesView {
	.search {
		margin: 12px 0 16px 24px;
		:deep(input) {
			font-size: 15px;
			line-height: 20px;
			font-weight: 400;
			height: 36px;
			min-height: unset;
		}
	}
}
</style>